import { ActionButtonPageTitle, Button } from '@strata/react-buttons';
import { RequestType, RequestTypeDisplayTextMap } from '@strata/react-chat';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { PageTitle } from '@strata/react-page-title';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { SubmitRequestDialog } from 'components/requests/dialogs/SubmitRequestDialog';
import { RequestArchiveGrid } from 'components/requests/RequestArchiveGrid';
import { RequestsGrid } from 'components/requests/RequestsGrid';
import { PayerRequestWritePermissions } from 'services/constants';
import { type BackToDestination, useGoBack } from 'services/navigationHelpers';
import { ReadPermissionRequestTypeMap } from 'services/payerRequests';
import { useAppSelector } from 'store';
import { useGetOpenRequestCountsQuery } from 'store/api/payerRequestApi';
import { useGetPayerDetailsQuery } from 'store/api/referenceApi';

const emptyBackToDestinationDefault = {
    route: '',
    text: 'Back',
};

export const RequestsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { backToDestination, goBack } = useGoBack(emptyBackToDestinationDefault);

    const [isSubmitRequestDialogOpen, setIsSubmitRequestDialogOpen] = useState(false);

    const { payerId, permissions } = useAppSelector((state) => state.auth);

    const { data: payerDetails } = useGetPayerDetailsQuery({ payerId: payerId! });

    const pbmOnlyAccess = permissions.includes(
        PayerRequestWritePermissions.WRITE_PAYER_REQUEST_PBM_ONLY,
    );

    const requestTypes = useMemo(
        () => [
            ...Object.entries(ReadPermissionRequestTypeMap)
                // Only show requestTypes user has permissions for
                .filter(([permission]) => permissions.includes(permission))
                .map(([_, requestType]) => requestType),
            // If user has access to page they can see archive
            RequestType.ARCHIVE,
        ],
        [permissions],
    );

    const activeRequestType =
        (location.hash
            .replace(/^#/, '')
            .toLocaleUpperCase() as (typeof RequestType)[keyof typeof RequestType]) ??
        requestTypes[0];

    useEffect(() => {
        if (!requestTypes.includes(activeRequestType)) {
            navigate(
                {
                    ...location,
                    hash: requestTypes[0]?.toLocaleLowerCase(),
                },
                {
                    state: location?.state as {
                        backToDestination?: BackToDestination;
                    },
                },
            );
        }
    }, [activeRequestType, requestTypes, location, navigate]);

    const { data: requestCounts } = useGetOpenRequestCountsQuery({ payerId: payerId! });

    return (
        <FormHeaderAndTabbedContent
            rowOneContentHeader={
                <div
                    className="flex__gap--medium"
                    style={{
                        flexGrow: 1,
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {backToDestination.route ? (
                            <ActionButtonPageTitle
                                label={backToDestination.text}
                                title="Requests"
                                id="requests-back-button"
                                secondaryTitle={payerDetails?.payerName}
                                onClick={() => goBack()}
                            />
                        ) : (
                            <PageTitle title="Requests" secondaryTitle={payerDetails?.payerName} />
                        )}
                    </div>
                    <SubmitRequestDialog
                        isOpen={isSubmitRequestDialogOpen}
                        onClose={() => setIsSubmitRequestDialogOpen(false)}
                    />
                    <Button
                        label="Submit Request"
                        id="submit-request-button"
                        intent={Button.Intent.PRIMARY}
                        disabled={Object.values(PayerRequestWritePermissions).every(
                            (permission) => !permissions.includes(permission),
                        )}
                        onClick={() => setIsSubmitRequestDialogOpen(true)}
                    />
                </div>
            }
            tabs={[
                ...requestTypes.map((requestType) => ({
                    title: RequestTypeDisplayTextMap[requestType],
                    className: 'tabbed-grid-content',
                    badgeCount: requestCounts?.[requestType],
                    mainContent:
                        requestType === RequestType.ARCHIVE ? (
                            <RequestArchiveGrid pbmOnlyAccess={pbmOnlyAccess} />
                        ) : (
                            <RequestsGrid requestType={requestType} pbmOnlyAccess={pbmOnlyAccess} />
                        ),
                })),
            ]}
            activeTab={RequestTypeDisplayTextMap[activeRequestType]}
            onTabSelect={(tab) =>
                navigate(
                    {
                        ...location,
                        hash: (
                            Object.keys(
                                RequestTypeDisplayTextMap,
                            ) as (typeof RequestType)[keyof typeof RequestType][]
                        )
                            .find((requestType) => RequestTypeDisplayTextMap[requestType] === tab)
                            ?.toLocaleLowerCase(),
                    },
                    {
                        state: location?.state as {
                            backToDestination?: BackToDestination;
                        },
                    },
                )
            }
        />
    );
};
